import { useState } from 'react';
import { Plus, Edit, Trash2, Camera, Upload, Loader2, Star, Image, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveMediaUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

export default function GalleriesTab({ events, galleries, onCreateNew, onGalleryDeleted, onRefresh }) {
  const { toast } = useToast();
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const openGalleryPhotos = async (gallery) => {
    setSelectedGallery(gallery);
    try {
      const response = await axios.get(`${API}/galleries/${gallery.id}/photos`);
      setGalleryPhotos(response.data);
    } catch (error) {
      console.error('Error fetching gallery photos:', error);
    }
  };

  const handleUploadPhotos = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedGallery) return;

    setUploadingPhotos(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      await axios.post(`${API}/galleries/${selectedGallery.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Sucesso', description: `${files.length} foto(s) enviada(s) com sucesso.` });
      const response = await axios.get(`${API}/galleries/${selectedGallery.id}/photos`);
      setGalleryPhotos(response.data);
      onRefresh();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível enviar as fotos.', variant: 'destructive' });
    } finally {
      setUploadingPhotos(false);
      e.target.value = '';
    }
  };

  const handleSetCoverPhoto = async (photoId) => {
    try {
      await axios.put(`${API}/galleries/${selectedGallery.id}/photos/${photoId}/cover`);
      toast({ title: 'Sucesso', description: 'Foto de capa definida.' });
      const response = await axios.get(`${API}/galleries/${selectedGallery.id}/photos`);
      setGalleryPhotos(response.data);
      onRefresh();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível definir a capa.', variant: 'destructive' });
    }
  };

  const handleDeleteGalleryPhoto = async (photoId) => {
    if (window.confirm('Tem certeza que deseja excluir esta foto?')) {
      try {
        await axios.delete(`${API}/galleries/${selectedGallery.id}/photos/${photoId}`);
        toast({ title: 'Sucesso', description: 'Foto excluída.' });
        const response = await axios.get(`${API}/galleries/${selectedGallery.id}/photos`);
        setGalleryPhotos(response.data);
        onRefresh();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir a foto.', variant: 'destructive' });
      }
    }
  };

  const handleToggleGalleryVisibility = async (gallery) => {
    const nextActive = gallery.is_active === false;
    try {
      await axios.put(`${API}/galleries/${gallery.id}`, null, {
        params: { is_active: nextActive }
      });
      toast({
        title: 'Sucesso',
        description: nextActive
          ? 'Galeria publicada novamente no site.'
          : 'Galeria ocultada do site sem apagar as fotos.',
      });
      if (selectedGallery?.id === gallery.id) {
        setSelectedGallery({ ...selectedGallery, is_active: nextActive });
      }
      onRefresh();
    } catch (error) {
      toast({ title: 'Erro', description: 'Não foi possível alterar a visibilidade da galeria.', variant: 'destructive' });
    }
  };

  const handleDeleteGallery = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta galeria e todas as suas fotos?')) {
      try {
        await axios.delete(`${API}/galleries/${id}`);
        toast({ title: 'Sucesso', description: 'Galeria excluída com sucesso.' });
        setSelectedGallery(null);
        setGalleryPhotos([]);
        onGalleryDeleted();
      } catch (error) {
        toast({ title: 'Erro', description: 'Não foi possível excluir a galeria.', variant: 'destructive' });
      }
    }
  };

  if (!selectedGallery) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-heading font-bold uppercase text-primary">Gerenciar Galerias</h2>
          <Button
            data-testid="create-gallery-button"
            onClick={onCreateNew}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nova Galeria
          </Button>
        </div>

        {galleries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.map((gallery) => (
              <div 
                key={gallery.id} 
                data-testid={`gallery-admin-${gallery.id}`} 
                className={`bg-card border border-border rounded-lg overflow-hidden group ${gallery.is_active === false ? 'opacity-70' : ''}`}
              >
                <div 
                  className="aspect-video relative cursor-pointer overflow-hidden"
                  onClick={() => openGalleryPhotos(gallery)}
                >
                  {gallery.cover_photo ? (
                    <img
                      src={resolveMediaUrl(gallery.cover_photo.thumbnail_url)}
                      alt={gallery.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-secondary flex items-center justify-center">
                      <Camera className="w-12 h-12 text-foreground/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent">
                    <div className="absolute top-3 left-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        gallery.is_active === false
                          ? 'bg-destructive text-destructive-foreground'
                          : 'bg-green-600 text-white'
                      }`}>
                        {gallery.is_active === false ? 'OCULTA' : 'PUBLICADA'}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-white font-bold">{gallery.event?.name || gallery.title}</h3>
                      <p className="text-white/70 text-sm">{gallery.photo_count} fotos</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 flex justify-between items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openGalleryPhotos(gallery)}
                    className="rounded-sm"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Gerenciar
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={gallery.is_active === false ? 'default' : 'outline'}
                      onClick={() => handleToggleGalleryVisibility(gallery)}
                      className="rounded-sm"
                      title={gallery.is_active === false ? 'Publicar galeria' : 'Ocultar galeria'}
                    >
                      {gallery.is_active === false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteGallery(gallery.id)}
                      className="rounded-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-card border border-border rounded-lg">
            <Camera className="w-12 h-12 mx-auto mb-4 text-foreground/30" />
            <p className="text-foreground/50">Nenhuma galeria criada ainda.</p>
          </div>
        )}
      </div>
    );
  }

  // Gallery detail / photo management view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => { setSelectedGallery(null); setGalleryPhotos([]); }}
            className="mb-2"
          >
            ← Voltar para Galerias
          </Button>
          <h2 className="text-2xl font-heading font-bold uppercase text-primary">
            {selectedGallery.event?.name || selectedGallery.title}
          </h2>
          <p className="text-foreground/70 text-sm">
            {galleryPhotos.length} fotos • {selectedGallery.is_active === false ? 'Oculta no site' : 'Publicada no site'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={selectedGallery.is_active === false ? 'default' : 'outline'}
            onClick={() => handleToggleGalleryVisibility(selectedGallery)}
            className="uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
          >
            {selectedGallery.is_active === false ? (
              <Eye className="w-5 h-5 mr-2" />
            ) : (
              <EyeOff className="w-5 h-5 mr-2" />
            )}
            {selectedGallery.is_active === false ? 'Publicar' : 'Ocultar'}
          </Button>
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleUploadPhotos}
              className="hidden"
              disabled={uploadingPhotos}
            />
            <Button
              asChild
              disabled={uploadingPhotos}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
            >
              <span>
                {uploadingPhotos ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-5 h-5 mr-2" />
                )}
                {uploadingPhotos ? 'Enviando...' : 'Upload Fotos'}
              </span>
            </Button>
          </label>
        </div>
      </div>

      {galleryPhotos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {galleryPhotos.map((photo) => (
            <div 
              key={photo.id} 
              data-testid={`gallery-photo-admin-${photo.id}`}
              className={`relative group rounded-lg overflow-hidden ${photo.is_cover ? 'ring-4 ring-accent' : ''}`}
            >
              <img
                src={resolveMediaUrl(photo.thumbnail_url || photo.url)}
                alt={photo.filename}
                className="w-full aspect-square object-cover"
              />
              {photo.is_cover && (
                <div className="absolute top-2 left-2 bg-accent text-accent-foreground px-2 py-1 rounded text-xs font-bold">
                  CAPA
                </div>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!photo.is_cover && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleSetCoverPhoto(photo.id)}
                    title="Definir como capa"
                  >
                    <Star className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteGalleryPhoto(photo.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-lg border-dashed">
          <Image className="w-12 h-12 mx-auto mb-4 text-foreground/30" />
          <p className="text-foreground/50 mb-4">Nenhuma foto nesta galeria.</p>
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleUploadPhotos}
              className="hidden"
            />
            <Button asChild variant="outline">
              <span>
                <Upload className="w-4 h-4 mr-2" />
                Fazer upload de fotos
              </span>
            </Button>
          </label>
        </div>
      )}
    </div>
  );
}
